import { Select } from "antd"
import { 
  DndContext, 
  DragEndEvent, 
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
  MeasuringStrategy
} from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { AddJobsRecord } from "../routingApi"
import { RouteLocation, formatTime, formatDistance, RouteViewState } from "./routeViewModel"
import { findRouteJobDetails, isRouteJobSold } from "../routingModel"
import SortableJob from "./SortableJob"

type RouteViewJobListProps = {
  state: RouteViewState
  totalTime: number
  distance: number
  locations: RouteLocation[]
  jobsForCreateRoute: AddJobsRecord[] | undefined
  tenantLocations?: any[]
  hoveredJobSortIndex: number | null
  setHoveredJobSortIndex: (sortIndex: number | null) => void
  onStartingLocationChange: (value: string) => void
  onEndingLocationChange: (value: string) => void
  onDragEnd: (event: DragEndEvent) => void
  onJobClick?: (jobId: number) => void
}

const RouteViewJobList = ({
  state,
  totalTime,
  distance,
  locations,
  jobsForCreateRoute,
  tenantLocations,
  hoveredJobSortIndex,
  setHoveredJobSortIndex,
  onStartingLocationChange,
  onEndingLocationChange,
  onDragEnd,
  onJobClick
}: RouteViewJobListProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  )

  return (
    <div>
      <div id="drive-time-display" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: '14px', color: '#666' }}>
        <span style={{ fontWeight: 500, marginLeft: 15 }}>Total Drive Time: {formatTime(totalTime)}</span>
        <span style={{ fontWeight: 500, marginRight: 30 }}>Distance: {formatDistance(distance)} miles</span>
      </div>

      {state.startingLocation && (
        <div id="starting-location-selector" style={{ 
          padding: '8px', 
          background: '#f5f5f5', 
          borderRadius: '8px',
          marginBottom: '16px',
          marginLeft: '15px',
          marginRight: '15px'
        }}>
          <div style={{ fontWeight: 500, marginBottom: '4px', fontSize: '14px' }}>Starting Location</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Select
              style={{ width: '100%', height: 65, marginBottom: 2 }}
              value={state.startingLocation?.id}
              onChange={onStartingLocationChange}
              options={locations ? locations.map(location => ({
                value: location.id,
                label: (
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontWeight: 500, color: '#000' }}>{location.jobName}</span>
                    <span style={{ fontSize: '12px', color: '#666' }}>
                      {location.address1}{location.address2 ? `, ${location.address2}` : ''}, {location.city}, {location.stateCode} {location.zip}
                    </span>
                  </div>
                )
              })) : []}
              placeholder="Select starting location"
              listHeight={200}
              dropdownStyle={{ 
                overflowY: 'scroll'
              }}
            />
          </div>
        </div>
      )}
      
      {/* Jobs List Area */}
      {state.jobs && state.jobs.length > 0 ? (
        <DndContext 
          onDragEnd={onDragEnd}
          sensors={sensors}
          collisionDetection={closestCenter}
          measuring={{
            droppable: {
              strategy: MeasuringStrategy.Always,
            },
          }}
        >
          <SortableContext
            items={state.jobs.map(job => job.sortIndex)}
            strategy={verticalListSortingStrategy}
          >
            <div style={{ padding: '0 15px' }}>
              {state.jobs
                .slice()
                .sort((a, b) => a.sortIndex - b.sortIndex)
                .map((routeJob) => {
                  if (routeJob.tenantLocationId) {
                    const tenantLocation = tenantLocations?.find(tl => tl.id === routeJob.tenantLocationId)
                    if (!tenantLocation) return null
                    
                    const isStartingLocation = state.startingLocation && state.startingLocation.id === `shop-${tenantLocation.id}`
                    const isEndingLocation = state.endingLocation && state.endingLocation.id === `shop-${tenantLocation.id}`
                    
                    if (isStartingLocation || isEndingLocation) {
                      return null
                    }
                    
                    const tenantLocationObj: AddJobsRecord = {
                      jobId: parseInt(`999${tenantLocation.id}`),
                      jobName: 'My Shop',
                      customerId: 0,
                      customerDisplayName: 'Shop',
                      workareaCount: 0,
                      allDivisionIds: '',
                      type: '0',
                      status: '0',
                      salesPersonEmail: '',
                      address1: tenantLocation.address1,
                      address2: tenantLocation.address2,
                      city: tenantLocation.city,
                      state: tenantLocation.stateCode,
                      zip: tenantLocation.zip,
                      county: null,
                      countryCode: null,
                      nickname: null,
                      workAreas: [],
                      propertyId: 0,
                      latitude: tenantLocation.latitude,
                      longitude: tenantLocation.longitude,
                      active: true
                    }
                    
                    const visibleIndex = state.jobs
                      .slice()
                      .sort((a, b) => a.sortIndex - b.sortIndex)
                      .filter((j) => {
                        if (j.tenantLocationId) {
                          const tl = tenantLocations?.find(tl => tl.id === j.tenantLocationId)
                          if (!tl) return false
                          const isStarting = state.startingLocation && state.startingLocation.id === `shop-${tl.id}`
                          const isEnding = state.endingLocation && state.endingLocation.id === `shop-${tl.id}`
                          return !isStarting && !isEnding
                        } else {
                          const job = findRouteJobDetails(j, jobsForCreateRoute)
                          if (!job) {
                            return false
                          }
                          const isStarting = state.startingLocation && String(j.jobId) === state.startingLocation.id.replace('job-', '')
                          const isEnding = state.endingLocation && String(j.jobId) === state.endingLocation.id.replace('job-', '')
                          return !isStarting && !isEnding
                        }
                      })
                      .findIndex(j => j.sortIndex === routeJob.sortIndex)

                    return (
                      <SortableJob
                        key={routeJob.sortIndex}
                        routeJob={routeJob}
                        job={tenantLocationObj}
                        isHovered={hoveredJobSortIndex === routeJob.sortIndex}
                        onMouseEnter={() => setHoveredJobSortIndex(routeJob.sortIndex)}
                        onMouseLeave={() => setHoveredJobSortIndex(null)}
                        disabled={false}
                        visibleIndex={visibleIndex}
                        onClick={() => onJobClick?.(tenantLocationObj.jobId)}
                      />
                    )
                  }
                  
                  const job = findRouteJobDetails(routeJob, jobsForCreateRoute)
                  if (!job) {
                    return null
                  }
                  
                  const isStartingLocation = state.startingLocation && String(routeJob.jobId) === state.startingLocation.id.replace('job-', '')
                  const isEndingLocation = state.endingLocation && String(routeJob.jobId) === state.endingLocation.id.replace('job-', '')
                  
                  if (isStartingLocation || isEndingLocation) {
                    return null
                  }
                  
                  const visibleIndex = state.jobs
                    .slice()
                    .sort((a, b) => a.sortIndex - b.sortIndex)
                    .filter(j => {
                      if (j.tenantLocationId) {
                        const tl = tenantLocations?.find(tl => tl.id === j.tenantLocationId)
                        if (!tl) return false
                        const isStarting = state.startingLocation && state.startingLocation.id === `shop-${tl.id}`
                        const isEnding = state.endingLocation && state.endingLocation.id === `shop-${tl.id}`
                        return !isStarting && !isEnding
                      } else {
                        const job = findRouteJobDetails(j, jobsForCreateRoute)
                        if (!job) {
                          return false
                        }
                        const isStarting = state.startingLocation && String(j.jobId) === state.startingLocation.id.replace('job-', '')
                        const isEnding = state.endingLocation && String(j.jobId) === state.endingLocation.id.replace('job-', '')
                        return !isStarting && !isEnding
                      }
                    })
                    .findIndex(j => j.sortIndex === routeJob.sortIndex)

                  return (
                    <SortableJob
                      key={routeJob.sortIndex}
                      routeJob={routeJob}
                      job={job}
                      isSold={isRouteJobSold(routeJob, jobsForCreateRoute)}
                      isHovered={hoveredJobSortIndex === routeJob.sortIndex}
                      onMouseEnter={() => setHoveredJobSortIndex(routeJob.sortIndex)}
                      onMouseLeave={() => setHoveredJobSortIndex(null)}
                      disabled={false}
                      visibleIndex={visibleIndex}
                      onClick={routeJob.jobId ? () => onJobClick?.(routeJob.jobId!) : undefined}
                    />
                  )
                })}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <div
          style={{
            border: '1.5px dashed #d3dae6',
            borderRadius: '12px',
            minHeight: '100px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#7a869a',
            fontSize: '16px',
            margin: '0 15px 16px 15px',
          }}
        >
          No Added Jobs
        </div>
      )}
      
      {state.endingLocation && (
        <div style={{ 
          padding: '8px', 
          background: '#f5f5f5', 
          borderRadius: '8px',
          marginBottom: '2px',
          marginLeft: '15px',
          marginRight: '15px'
        }}>
          <div style={{ fontWeight: 500, marginBottom: '4px', fontSize: '14px' }}>Ending Location</div>
          <Select
            style={{ width: '100%', marginBottom: 2, height: 65 }}
            value={state.endingLocation?.id}
            onChange={onEndingLocationChange}
            options={locations ? locations.map(location => ({
              value: location.id,
              label: (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontWeight: 500, color: '#000' }}>{location.jobName}</span>
                  <span style={{ fontSize: '12px', color: '#666' }}>
                    {location.address1}{location.address2 ? `, ${location.address2}` : ''}, {location.city}, {location.stateCode} {location.zip}
                  </span>
                </div>
              )
            })) : []}
            placeholder="Select ending location"
            listHeight={200}
            dropdownStyle={{ 
              overflowY: 'scroll'
            }}
          />
        </div>
      )}
    </div>
  )
}

export default RouteViewJobList